import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import defaultContent from "../../../../content/hat";
import { PageTitleSection } from "../../../../common/components/PageTitleSection/PageTitleSection";
import LegsTrackingTable from "../../LegsTrackingTable/LegsTrackingTable";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { FetchLegsTrackingData } from "../LegsTrackingDataLayer";
import { IconBack } from "../../../../common/icons/IconBack/IconBack";
import LeafBreadcrumbs from "../../../../common/leaf/LeafBreadcrumbs/LeafBreadcrumbs";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import LeafTable from "../../../../common/leaf/LeafTable/LeafTable";
import "./HatLegsTracking.scss";
import { getFullMonth, getMonthAndYear } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import { BonusInfoFooter } from "../../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { SELECTOR_TYPE, updateSelectorType } from "../../../../reducers/period";
import { HATTimeoutError } from "../../ErrorPages/HATTimeoutError";

const HatLegsTracking = () => {
  const { t } = useTranslation(["hat"]);
  const dispatch = useAppDispatch();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const hideInternationalLegs = useAppSelector((state) => state.boot.configuration?.hideInternationalInfo) === true;
  const locale = storedLocale();
  const { legsTrackingData, isLoading, refetch, errorStatus } = FetchLegsTrackingData(selectedPeriod);
  const previousPY = legsTrackingData.PreviousPYTotals.PerformanceYear.toString().slice(2, 4);
  const currentPY = legsTrackingData.SelectedPYTotals.PerformanceYear.toString().slice(2, 4);
  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.PY));
  }, [dispatch]);

  // Markets without international legs (e.g. VN): the API returns in-market-only totals, so the
  // In-Market column would duplicate Total and is dropped.
  const inMarketOnly = hideInternationalLegs;
  const columnWidth = inMarketOnly ? "33%" : "25%";
  const align = "center" as "center" | "left" | "right";
  const buildTotalsRow = (performanceYear: string, totals: typeof legsTrackingData.SelectedPYTotals) => ({
    row: [
      { value: t("legTrackingView.py", defaultContent.legTrackingView.py, { performanceYear }), columnAlign: align },
      ...(inMarketOnly ? [] : [{ value: totals.InMarketTotal, columnAlign: align }]),
      { value: inMarketOnly ? totals.InMarketTotal : totals.TotalQLegs, columnAlign: align },
      { value: totals.FoundersLegs, columnAlign: align },
    ],
  });

  const totalsTable = {
    headers: [
      {
        value: t(
          "legTrackingView.totalsSection.year",
          String(defaultContent["legTrackingView"]["totalsSection"]["year"]),
        ),
        width: columnWidth,
        columnAlign: align,
      },
      ...(inMarketOnly
        ? []
        : [
            {
              value: t(
                "legTrackingView.totalsSection.inMarket",
                String(defaultContent["legTrackingView"]["totalsSection"]["inMarket"]),
              ),
              width: columnWidth,
              columnAlign: align,
            },
          ]),
      {
        value: t(
          "legTrackingView.totalsSection.total",
          String(defaultContent["legTrackingView"]["totalsSection"]["total"]),
        ),
        width: columnWidth,
        columnAlign: align,
      },
      {
        value: t(
          "legTrackingView.totalsSection.founders",
          String(defaultContent["legTrackingView"]["totalsSection"]["founders"]),
        ),
        width: columnWidth,
        columnAlign: align,
      },
    ],
    rows: [
      buildTotalsRow(currentPY, legsTrackingData.SelectedPYTotals),
      buildTotalsRow(previousPY, legsTrackingData.PreviousPYTotals),
    ],
  };

  const { year: yearCurrentProcessingPeriod } = getMonthAndYear(legsTrackingData.CurrentProcessingPeriod);
  const fullMonthCurrentProcessingPeriod = getFullMonth(legsTrackingData.CurrentProcessingPeriod, locale);

  if (errorStatus) {
    return (
      <div className="HatLegsTracking">
        <PageTitleSection
          title={t("legTrackingView.legTracking", String(defaultContent["legTrackingView"]["legTracking"]))}
        />

        <div className="HatLegsTracking__route-back">
          <IconBack width="0.6rem" height="0.8rem" color="#38539a" />
          <LeafBreadcrumbs paths={[{ label: `${t("pageTitle", defaultContent.pageTitle as string)}`, url: "/hat" }]} />
        </div>

        <HATTimeoutError refetchFunction={refetch} />
        <BonusInfoFooter />
      </div>
    );
  }

  return (
    <div className="HatLegsTracking">
      <PageTitleSection
        title={t("legTrackingView.legTracking", String(defaultContent["legTrackingView"]["legTracking"]))}
      />

      <div className="HatLegsTracking__route-back">
        <IconBack width="0.6rem" height="0.8rem" color="#38539a" />
        <LeafBreadcrumbs paths={[{ label: `${t("pageTitle", defaultContent.pageTitle as string)}`, url: "/hat" }]} />
      </div>

      <div className="HatLegsTracking__table-section">
        {isLoading || legsTrackingData.CurrentProcessingPeriod === 0 ? (
          <>
            <div className="HatLegsTracking__totals-table">
              <LeafRectangleLoader isLoading={true} height="2rem" width="95%" margin="1rem 1rem 0.5rem 1rem" />
              <LeafRectangleLoader isLoading={true} height="1.5rem" width="95%" margin="0.5rem 1rem" />
              <LeafRectangleLoader isLoading={true} height="1.5rem" width="95%" margin="0.5rem 1rem 1rem" />
            </div>
            <div className="HatLegsTracking__tracking-tables">
              <LeafRectangleLoader isLoading={true} height="2rem" width="95%" margin="0.4rem" />
              <LeafRectangleLoader isLoading={true} height="1.5rem" width="95%" margin="0.5rem 0.3rem" />
              <LeafRectangleLoader isLoading={true} height="1.5rem" width="95%" margin="0.5rem 0.3rem" />
              <LeafRectangleLoader isLoading={true} height="1.5rem" width="95%" margin="0.5rem 0.3rem" />
            </div>
          </>
        ) : (
          <>
            <div className="HatLegsTracking__totals-table">
              <LeafTable
                header={totalsTable.headers}
                rows={totalsTable.rows}
                columnAlign={"left"}
                headerStyle={"default"}
                hideHeader={false}
              />
            </div>

            <div className="HatLegsTracking__current-month">
              {
                <Trans>
                  {t(
                    "legTrackingView.currentMonthProcessing",
                    defaultContent["legTrackingView"]["currentMonthProcessing"],
                    {
                      month: fullMonthCurrentProcessingPeriod + " " + yearCurrentProcessingPeriod,
                    },
                  )}
                  <span className="HatLegsTracking__date">{/* month */}</span>
                </Trans>
              }
            </div>

            <div className="HatLegsTracking__tracking-tables">
              {legsTrackingData.InMarketQLegs.Legs &&
                (legsTrackingData.InMarketQLegs.PreviousPYMonthlyTotals.length > 0 ||
                  legsTrackingData.InMarketQLegs.SelectedPYMonthlyTotals.length > 0) && (
                  <div className="HatLegsTracking__section-container">
                    <div className="HatLegsTracking__section-title">
                      {t("legTrackingView.inMarketLegs", String(defaultContent["legTrackingView"]["inMarketLegs"]))}
                    </div>
                    <div className="HatLegsTracking__table-container">
                      <LegsTrackingTable
                        QLegList={legsTrackingData.InMarketQLegs}
                        currentProcessingPeriod={legsTrackingData.CurrentProcessingPeriod.toString()}
                        isInMarket={true}
                        isInternational={false}
                        isNonQualifiedInMarket={false}
                      />
                    </div>
                  </div>
                )}

              {!hideInternationalLegs &&
                legsTrackingData.InternationalQLegs.Legs &&
                (legsTrackingData.InternationalQLegs.PreviousPYMonthlyTotals.length > 0 ||
                  legsTrackingData.InternationalQLegs.SelectedPYMonthlyTotals.length > 0) && (
                  <div className="HatLegsTracking__section-container HatLegsTracking__margin">
                    <div className="HatLegsTracking__section-title">
                      {t(
                        "legTrackingView.internationalLegs",
                        String(defaultContent["legTrackingView"]["internationalLegs"]),
                      )}
                    </div>

                    <div className="HatLegsTracking__table-container">
                      <LegsTrackingTable
                        QLegList={legsTrackingData.InternationalQLegs}
                        currentProcessingPeriod={legsTrackingData.CurrentProcessingPeriod.toString()}
                        isInMarket={false}
                        isInternational={true}
                        isNonQualifiedInMarket={false}
                      />
                    </div>
                  </div>
                )}

              {legsTrackingData.InMarketNonQLegsAbove9Percent.Legs &&
                (legsTrackingData.InMarketNonQLegsAbove9Percent.PreviousPYMonthlyTotals.length > 0 ||
                  legsTrackingData.InMarketNonQLegsAbove9Percent.SelectedPYMonthlyTotals.length > 0) && (
                  <div className="HatLegsTracking__section-container HatLegsTracking__margin">
                    <div className="HatLegsTracking__section-title">
                      {t(
                        "legTrackingView.nonQualifiedMarket",
                        String(defaultContent["legTrackingView"]["nonQualifiedMarket"]),
                      )}
                    </div>
                    <div className="HatLegsTracking__table-container">
                      <LegsTrackingTable
                        QLegList={legsTrackingData.InMarketNonQLegsAbove9Percent}
                        currentProcessingPeriod={legsTrackingData.CurrentProcessingPeriod.toString()}
                        isInMarket={false}
                        isInternational={false}
                        isNonQualifiedInMarket={true}
                      />
                    </div>
                  </div>
                )}
            </div>

            <div className="HatLegsTracking__message">
              {t(
                "legTrackingView.showsNoQualification",
                String(defaultContent["legTrackingView"]["showsNoQualification"]),
              )}
            </div>
          </>
        )}
      </div>
      <BonusInfoFooter />
    </div>
  );
};

export default HatLegsTracking;
